import {
  isPersistedRecord,
  runPersistMigrations,
  type PersistMigrationStep,
} from '../persist-migration';
import { mergeKeyedRecords } from '../merge-strategies';
import { migrateTabScopedStoreKey } from '../target-scope-key.migration';

type TabScopedEntries = Record<string, Record<string, unknown>>;

export const TAB_SCOPED_PERSIST_VERSION = 5;

const FILTER_KEYS = [
  'confidenceFilter',
  'categoryFilter',
  'originFilter',
  'assigneeFilter',
] as const;

const STATUS_MULTI_SELECT_VALUES = new Set([
  'ALL',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'PAUSED',
  'ABORTED',
  'BLOCKED',
  'HOT',
  'COLD',
  'PASSED',
  'EXPLOITED',
  'DUPLICATE',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toMultiSelectArray(value: unknown): unknown {
  if (value === 'ALL') {
    return [];
  }

  if (typeof value === 'string') {
    return [value];
  }

  return value;
}

const TAB_SCOPED_MIGRATION_STEPS: PersistMigrationStep<TabScopedEntries>[] = [
  {
    version: 2,
    migrate: (entries) =>
      Object.fromEntries(
        Object.entries(entries).flatMap(([scopeKey, entry]) => {
          if (!isRecord(entry)) {
            return [];
          }

          const nextEntry: Record<string, unknown> = { ...entry };

          for (const key of FILTER_KEYS) {
            if (Object.prototype.hasOwnProperty.call(nextEntry, key)) {
              nextEntry[key] = toMultiSelectArray(nextEntry[key]);
            }
          }

          if (
            typeof nextEntry.statusFilter === 'string' &&
            STATUS_MULTI_SELECT_VALUES.has(nextEntry.statusFilter)
          ) {
            nextEntry.statusFilter = toMultiSelectArray(nextEntry.statusFilter);
          }

          return [[scopeKey, nextEntry]];
        }),
      ),
  },
  {
    version: 3,
    migrate: (entries) =>
      mergeKeyedRecords({
        records: entries,
        migrateKey: migrateTabScopedStoreKey,
        merge: { type: 'object' },
      }),
  },
  {
    version: 4,
    migrate: (entries) =>
      Object.fromEntries(
        Object.entries(entries).map(([scopeKey, entry]) => {
          if (!isRecord(entry)) {
            return [scopeKey, entry];
          }

          const sourceFilter = entry.sourceFilter;

          if (
            Array.isArray(sourceFilter) &&
            sourceFilter.length === 1 &&
            sourceFilter[0] === 'proxy'
          ) {
            return [scopeKey, { ...entry, sourceFilter: ['proxy', 'copilot'] }];
          }

          return [scopeKey, entry];
        }),
      ),
  },
  {
    version: 5,
    migrate: (entries) =>
      Object.fromEntries(
        Object.entries(entries).map(([scopeKey, entry]) => {
          if (!isRecord(entry)) {
            return [scopeKey, entry];
          }

          const nextEntry: Record<string, unknown> = { ...entry };

          if (typeof nextEntry.methodFilter === 'string') {
            const isRequestsEntry =
              Object.prototype.hasOwnProperty.call(nextEntry, 'statusFilter') ||
              Object.prototype.hasOwnProperty.call(
                nextEntry,
                'mimeTypeFilter',
              ) ||
              Object.prototype.hasOwnProperty.call(
                nextEntry,
                'extensionFilterMode',
              ) ||
              Object.prototype.hasOwnProperty.call(nextEntry, 'tqlQuery');

            if (
              Object.prototype.hasOwnProperty.call(nextEntry, 'typeFilter') ||
              !isRequestsEntry
            ) {
              delete nextEntry.methodFilter;
            } else {
              nextEntry.methodFilter = toMultiSelectArray(
                nextEntry.methodFilter,
              );

              if (typeof nextEntry.statusFilter === 'string') {
                nextEntry.statusFilter = toMultiSelectArray(
                  nextEntry.statusFilter,
                );
              }

              if (typeof nextEntry.mimeTypeFilter === 'string') {
                nextEntry.mimeTypeFilter = toMultiSelectArray(
                  nextEntry.mimeTypeFilter,
                );
              }
            }
          }

          return [scopeKey, nextEntry];
        }),
      ),
  },
];

export function migrateTabScopedEntries({
  entries,
  fromVersion = 0,
}: {
  entries: TabScopedEntries;
  fromVersion?: number;
}): TabScopedEntries {
  return runPersistMigrations({
    state: entries,
    fromVersion,
    toVersion: TAB_SCOPED_PERSIST_VERSION,
    steps: TAB_SCOPED_MIGRATION_STEPS,
  });
}

function sanitizePersistedEntries(entries: unknown): TabScopedEntries {
  if (!isRecord(entries)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(entries).flatMap(([scopeKey, entryValue]) => {
      if (!isRecord(entryValue)) {
        return [];
      }

      return [[scopeKey, entryValue]];
    }),
  );
}

type PersistedTabScopedState = {
  entries: TabScopedEntries;
};

export function migrateTabScopedPersistedState(
  persistedState: unknown,
  version: number,
): PersistedTabScopedState {
  if (!isPersistedRecord(persistedState)) {
    return { entries: {} };
  }

  const state = persistedState as Partial<PersistedTabScopedState>;
  const entries = sanitizePersistedEntries(state.entries);

  if (version < TAB_SCOPED_PERSIST_VERSION) {
    return {
      entries: migrateTabScopedEntries({ entries, fromVersion: version }),
    };
  }

  return { entries };
}
